const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8001/api';

class ApiService {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    return this.token || localStorage.getItem('admin_token');
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const token = this.getToken();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.clearToken();
          window.location.href = '/login';
        }
        throw new Error(data.message || data.error || 'Ошибка запроса');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, body) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put(endpoint, body) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Auth
  async login(username, password) {
    const data = await this.post('/auth/login', { username, password });
    if (data.token) {
      this.setToken(data.token);
      localStorage.setItem('admin_token', data.token);
    }
    return data;
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } finally {
      this.clearToken();
    }
  }

  async checkAuth() {
    return this.get('/auth/me');
  }

  // Analytics
  async getAnalyticsSummary() {
    return this.get('/analytics/summary');
  }

  async getAnalyticsCharts(period = '7days') {
    return this.get(`/analytics/charts?period=${period}`);
  }

  async getModelStats() {
    return this.get('/analytics/models');
  }

  async getRetention() {
    return this.get('/analytics/retention');
  }

  async getHourlyActivity() {
    return this.get('/analytics/activity/hourly');
  }

  async getActiveUsers(period = '7days', limit = 20) {
    const params = { period, limit: String(limit) };
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/analytics/active-users?${queryString}`);
  }

  async getActivationFunnel(period = '30days') {
    return this.get(`/analytics/activation-funnel?period=${period}`);
  }

  async getAdMonetizationFunnel(period = '30days') {
    return this.get(`/analytics/ad-monetization-funnel?period=${period}`);
  }

  async getProviderStats() {
    return this.get('/analytics/providers');
  }

  async getProviderChart(period = '7days') {
    return this.get(`/analytics/providers/chart?period=${period}`);
  }

  // Model Settings
  async getModelSettings() {
    return this.get('/model-settings');
  }

  async updateModelSettings(settings) {
    return this.put('/model-settings', settings);
  }

  // Users
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/users${queryString ? '?' + queryString : ''}`);
  }

  async getUser(id) {
    return this.get(`/users/${id}`);
  }

  async blockUser(id) {
    return this.put(`/users/${id}/block`);
  }

  async unblockUser(id) {
    return this.put(`/users/${id}/unblock`);
  }

  async updateUserBalance(id, balance) {
    return this.put(`/users/${id}/balance`, { balance });
  }

  async deleteUser(id) {
    return this.delete(`/users/${id}`);
  }

  async getUserAdHistory(id, page = 1, limit = 20) {
    return this.get(`/users/${id}/ads?page=${page}&limit=${limit}`);
  }

  // Chats
  async getChats(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/chats${queryString ? '?' + queryString : ''}`);
  }

  async getChat(id) {
    return this.get(`/chats/${id}`);
  }

  async deleteChat(id) {
    return this.delete(`/chats/${id}`);
  }

  // Feedback
  async getFeedback(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/feedback${queryString ? '?' + queryString : ''}`);
  }

  async markFeedbackAsRead(id) {
    return this.put(`/feedback/${id}/read`);
  }

  async deleteFeedback(id) {
    return this.delete(`/feedback/${id}`);
  }

  // Images
  async getImages(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/images${queryString ? '?' + queryString : ''}`);
  }

  async getImageStats() {
    return this.get('/images/stats');
  }

  // Notifications
  async getNotifications(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/notifications${queryString ? '?' + queryString : ''}`);
  }

  async getNotificationStats() {
    return this.get('/notifications/stats');
  }

  async getNotificationRewards(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/notifications/rewards${queryString ? '?' + queryString : ''}`);
  }

  async deleteNotification(id) {
    return this.delete(`/notifications/${id}`);
  }

  // Traffic Sources
  async getTrafficSourceStats() {
    return this.get('/traffic-sources/stats');
  }

  async getTrafficSourceList(page = 1, limit = 20, filterType = null) {
    const params = { page: String(page), limit: String(limit) };
    if (filterType) {
      params.filter_type = filterType;
    }
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/traffic-sources/list${queryString ? '?' + queryString : ''}`);
  }

  async getTrafficSourceDashboardSummary() {
    return this.get('/traffic-sources/dashboard-summary');
  }

  // Images
  async getImageStats() {
    return this.get('/images/stats');
  }

  async getImageList(page = 1, limit = 20, filterVkUserId = null) {
    const params = { page: String(page), limit: String(limit) };
    if (filterVkUserId) {
      params.filter_vk_user_id = filterVkUserId;
    }
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/images/list?${queryString}`);
  }

  async deleteImage(id) {
    return this.delete(`/images/${id}`);
  }

  // Recommendations
  async getRecommendationsSummary() {
    return this.get('/recommendations/summary');
  }

  async getRecommendationsCharts(period = '30days') {
    return this.get(`/recommendations/charts?period=${period}`);
  }

  async getRecommendationsFunnel() {
    return this.get('/recommendations/funnel');
  }

  async getRecommendationsUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/recommendations/users${queryString ? '?' + queryString : ''}`);
  }

  async getRecommendationsTopUsers(limit = 10) {
    return this.get(`/recommendations/top-users?limit=${limit}`);
  }

  // Games
  async getGames() {
    return this.get('/games');
  }

  async getGamesSummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/games/summary${queryString ? '?' + queryString : ''}`);
  }

  async getGameSessions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/games/sessions${queryString ? '?' + queryString : ''}`);
  }

  async updateGame(gameId, payload) {
    return this.put(`/games/${encodeURIComponent(gameId)}`, payload);
  }

  // Group Subscriptions
  async getGroupSubscriptionSummary() {
    return this.get('/group-subscriptions/summary');
  }

  async getGroupSubscriptionCharts(period = '30days') {
    return this.get(`/group-subscriptions/charts?period=${period}`);
  }

  async getGroupSubscriptionUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/group-subscriptions/users${queryString ? '?' + queryString : ''}`);
  }
}

export const api = new ApiService();
